import { TableOptions } from '@/types/table';
import { Row, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import styles from '../Table.module.scss';
import { ColumnOffsets, PositionedColumnDef } from '../utils/columnLayout';
import { DEFAULT_PAGE_SIZE } from '../utils/constants';
import LoadedRows from './LoadedRows';
import LoadingRows from './LoadingRows';

export interface TableRowsProps<T> {
  table: Table<T>;
  isLoading: boolean;
  options?: TableOptions<T>;
  onToggleRow: (row: Row<T>) => void;
  rowExpandedDelayed: Record<string, boolean>;
  columnCount: number;
  tableId: string;
  positionedColumnDefs: PositionedColumnDef<T>[];
  columnOffsets: ColumnOffsets;
  searchHighlightValue?: string;
}

const TableRows = <T,>({
  table,
  isLoading,
  options,
  onToggleRow,
  rowExpandedDelayed,
  columnCount,
  tableId,
  positionedColumnDefs,
  columnOffsets,
  searchHighlightValue,
}: TableRowsProps<T>) => {
  const { t } = useTranslation();
  const showNoDataMsg = !isLoading && table.getRowModel().rows.length === 0;
  const numberColumns = columnCount + (options?.rowExtension ? 1 : 0);
  return (
    <tbody>
      {isLoading ? (
        <LoadingRows pageSize={options?.pageSize ?? DEFAULT_PAGE_SIZE} columnCount={numberColumns} />
      ) : showNoDataMsg ? (
        <tr>
          <td colSpan={numberColumns} className={styles.noContent} data-testid="rowNoContent">
            {t('global.table.noData')}
          </td>
        </tr>
      ) : (
        <LoadedRows
          table={table}
          tableId={tableId}
          options={options}
          onToggleRow={onToggleRow}
          rowExpandedDelayed={rowExpandedDelayed}
          positionedColumnDefs={positionedColumnDefs}
          columnOffsets={columnOffsets}
          searchHighlightValue={searchHighlightValue}
        />
      )}
    </tbody>
  );
};

export default TableRows;
